import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import CheckIcon from '@mui/icons-material/Check';
import WarningIcon from '@mui/icons-material/Warning';
import DangerousIcon from '@mui/icons-material/Dangerous';
import PlusOneIcon from '@mui/icons-material/PlusOne';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { useRecordContext, Labeled } from 'react-admin';
import { useOnSettled } from '../therapistAppConfig/useOnSettled';
import { GenericRelativeDateDisplay } from '@/components/GenericDateFunctionField';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import { useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { get } from 'lodash';
import { CreditCardOff } from '@mui/icons-material';
import { RawDataButton } from '@/components/RawDataButton';
import { adminRpcClient } from '@/providers/backendClient';
import { OpenInNew } from '@mui/icons-material';
import { useCreateNavigationTargetPath } from '@/components/useCreateNavigationTargetPath';

export const AccountInfoActions = () => {
  const record = useRecordContext();

  const { onSettled } = useOnSettled();

  const { isPending, mutateAsync: onSyncSubscription } = useMutation({
    mutationKey: ['PUBLIC_ADMIN_syncRevenueCat', record.id],
    mutationFn: async () => {
      console.warn('RECORD ID', record.id);

      await adminRpcClient('PUBLIC_ADMIN_syncRevenueCat', [
        record.id,
      ]);
    },
    onSettled,
  });

  const appTrackerPath = useCreateNavigationTargetPath(
    'appTrackerEvents',
    {
      userId: record.id,
    },
  );

  return (
    <Box
      display="flex"
      alignItems="center"
      gap="0.5rem"
      onClick={e => e.stopPropagation()}
    >
      <RawDataButton entity={record} title="Raw Account Data" />
      <Tooltip title="Syncs subscription data from RevenueCat and immediately syncs to Customer.io via trigger">
        <Button
          endIcon={
            isPending ? (
              <CircularProgress size={18} />
            ) : (
              <CloudSyncIcon />
            )
          }
          size="small"
          disabled={isPending}
          onClick={onSyncSubscription as any}
        >
          {`Sync${isPending ? 'ing' : ''} external metadata`}
        </Button>
      </Tooltip>
      <Button
        endIcon={<OpenInNew />}
        href={appTrackerPath}
        variant="outlined"
        autoCapitalize="none"
        size="small"
      >
        Event Tracker
      </Button>
    </Box>
  );
};

export const AccountInfo = () => {
  const record = useRecordContext();

  const hasFlags = useMemo(
    () =>
      record.accountInfo.isPendingCancellation ||
      record.accountInfo.isReactivated ||
      record.accountInfo.hasBillingIssues,
    [record],
  );

  return (
    <>
      <Box>
          <Labeled label="Service Availability">
            {renderServiceAvailability(record)}
          </Labeled>
          <Labeled label="Trial" marginLeft="1rem">
            {renderTrialSummary(record)}
          </Labeled>
          <Labeled label="Is Customer?" marginLeft="1rem">
            {renderCustomerSummary(record)}
          </Labeled>
          <Labeled
            label="Is PAID Subscription Active?"
            marginLeft="1rem"
          >
            {renderPaidSubscriptionSummary(record)}
          </Labeled>
          {record.accountInfo.hasFreeSubscription && (
            <Labeled
              label="FREE Subscription Expiration"
              marginLeft="1rem"
            >
              {renderFreeSubscriptionSummary(record)}
            </Labeled>
          )}
          {hasFlags && (
            <Labeled label="Flags" marginLeft="1rem">
              <>
                {renderPendingCancellationSummary(record, true)}
                {renderReactivatedFlag(record, true)}
                {renderBillingIssuesFlag(record)}
              </>
            </Labeled>
          )}
          {record.firstKnownAppVersion && (
            <Labeled marginLeft="1rem" label="First known version">
              <Chip
                label={record.firstKnownAppVersion}
                sx={{ fontSize: '0.875rem' }}
              />
            </Labeled>
          )}
          {record.lastKnownAppVersion &&
            record.firstKnownAppVersion !==
              record.lastKnownAppVersion && (
              <Labeled marginLeft="1rem" label="Last known version">
                <Chip
                  label={record.lastKnownAppVersion}
                  sx={{ fontSize: '0.875rem' }}
                />
              </Labeled>
            )}
          {record.timezoneInfo && (
            <Labeled marginLeft="1rem" label="Timezone">
              <Chip
                label={`${record.timezoneInfo.name} (${record.timezoneInfo.gmt})`}
                sx={{ fontSize: '0.875rem' }}
              />
            </Labeled>
          )}
        </Box>
    </>
  );
};

export const renderServiceAvailability = (record: any) => (
  <Chip
    icon={
      record.accountInfo.isActive ? <CheckIcon /> : <DangerousIcon />
    }
    color={record.accountInfo.isActive ? 'success' : 'warning'}
    label={record.accountInfo.isActive ? 'Available' : 'Suspended'}
    sx={{ fontSize: '0.875rem' }}
  />
);

export const accountStatuses = [
  'lifelong-trial',
  'free-subscription',
  'trialing',
  'trial-completed',
  'customer',
  'pending-cancellation',
  'billing-issues',
  'cancelled',
  'reactivated',
] as const;

type AccountStatus = (typeof accountStatuses)[number];

export const accountStatusTitleMap: Record<AccountStatus, string> = {
  'pending-cancellation': 'Pending Cancellation',
  'billing-issues': 'Billing Issues',
  'trial-completed': 'Trial Completed',
  cancelled: 'Cancelled',
  reactivated: 'Reactivated',
  customer: 'Active Customer',
  trialing: 'Ongoing Trial',
  'lifelong-trial': 'Lifelong Trial',
  'free-subscription': 'Free Subscription',
};

export const accountStatusTitleMapReverse = Object.fromEntries(
  Object.entries(accountStatusTitleMap).map(
    ([key, value]) => [value, key] as const,
  ),
);

export const renderAccountStatusSummary = (doc: any) => {
  const warningStatuses = [
    'pending-cancellation',
    'billing-issues',
    'trial-completed',
  ];

  const errorStatuses = ['cancelled'];

  const successStatuses = ['reactivated', 'customer'];

  const neutralStatuses = [
    'trialing',
    'lifelong-trial',
    'free-subscription',
  ];

  const color = (() => {
    if (warningStatuses.includes(doc.accountInfo.status)) {
      return 'warning';
    }

    if (errorStatuses.includes(doc.accountInfo.status)) {
      return 'error';
    }

    if (successStatuses.includes(doc.accountInfo.status)) {
      return 'success';
    }

    if (neutralStatuses.includes(doc.accountInfo.status)) {
      return 'info';
    }

    return 'default';
  })();

  const label =
    (accountStatusTitleMap as any)[doc.accountInfo.status] ||
    'Unknown';

  return <Chip label={label} color={color} sx={{ fontSize: '0.875rem' }} />;
};

export const renderTrialSummary = (record: any) => (
  <Chip
    icon={
      record.accountInfo.isTrialing ? (
        record.accountInfo.isLifelongTrial ? (
          <WarningIcon />
        ) : (
          <HourglassBottomIcon />
        )
      ) : (
        <CheckIcon />
      )
    }
    color={
      record.accountInfo.isTrialing
        ? record.accountInfo.isLifelongTrial
          ? 'warning'
          : 'secondary'
        : 'primary'
    }
    label={
      record.accountInfo.isTrialing
        ? `${record.accountInfo.isLifelongTrial ? 'Lifelong' : 'Ongoing'} Trial`
        : 'Trial Completed'
    }
    sx={{ fontSize: '0.875rem' }}
  />
);

export const renderCustomerSummary = (record: any) => (
  <Chip
    icon={
      record.accountInfo.isCustomer ? <CheckIcon /> : <WarningIcon />
    }
    color={record.accountInfo.isCustomer ? 'success' : 'error'}
    label={
      record.accountInfo.isCustomer
        ? 'Paying Customer'
        : 'Not a Customer'
    }
    sx={{ fontSize: '0.875rem' }}
  />
);

export const renderPaidSubscriptionSummary = (record: any) => (
  <Chip
    icon={
      record.accountInfo.isSubscriptionActive ? (
        <CheckIcon />
      ) : (
        <WarningIcon />
      )
    }
    color={
      record.accountInfo.isSubscriptionActive ? 'success' : 'warning'
    }
    label={record.accountInfo.isSubscriptionActive ? 'Yes' : 'No'}
    sx={{ fontSize: '0.875rem' }}
  />
);

export const renderPendingCancellationSummary = (
  record: any,
  margin = false,
) =>
  record.accountInfo.isPendingCancellation ? (
    <Chip
      sx={{
        fontSize: '0.875rem',
        ...(margin ? { marginRight: '1rem' } : {}),
      }}
      icon={<WarningIcon />}
      color="warning"
      label="Pending Cancellation"
    />
  ) : null;

export const renderNumericDate = (record: any, path: string) => {
  const value = get(record, path);

  if (!value) {
    return '';
  }

  try {
    return new Date(value).toISOString();
  } catch (e) {
    return '';
  }
};

export const renderReactivatedFlag = (record: any, margin = false) =>
  !!record.accountInfo.isReactivated ? (
    <Chip
      icon={<PlusOneIcon />}
      sx={{
        fontSize: '0.875rem',
        ...(margin ? { marginLeft: '1rem' } : {}),
      }}
      color="success"
      label="Reactivated"
    />
  ) : null;

export const renderBillingIssuesFlag = (
  record: any,
  margin = false,
) =>
  !!record.accountInfo.hasBillingIssues ? (
    <Chip
      icon={<CreditCardOff />}
      sx={{
        fontSize: '0.875rem',
        ...(margin ? { marginLeft: '1rem' } : {}),
      }}
      color="warning"
      label="Billing Issues"
    />
  ) : null;

export const renderFreeSubscriptionSummary = (record: any) =>
  record.accountInfo?.hasFreeSubscription ? (
    <GenericRelativeDateDisplay
      chipProps={{
        icon: record.accountInfo?.freeSubscriptionExpired ? (
          <WarningIcon />
        ) : (
          <HourglassBottomIcon />
        ),
        color: record.accountInfo?.freeSubscriptionExpired
          ? 'warning'
          : 'primary',
      }}
      date={record.accountInfo?.freeSubscriptionExpirationDate}
    />
  ) : (
    <></>
  );
